import { ReactNode, MouseEvent, useCallback } from 'react';
import sortBy from 'lodash/sortBy';

import {
  InquisitiveEnglishContentArea,
  InquisitiveYears,
} from 'global/graphql/scalars.type';
import type { SubjectFragment } from '../graphql/startup-data/startup-data.graphql.generated';
import { EnglishContentArea } from 'global/constants';
import { isKeyboardModifierClick } from 'lib/browser';
import { formatEnglishContentAreasList } from 'approot/shared/logic/format-english-content-area';
import { useYearTranslation } from 'approot/shared/logic/years';
import { InlineSvgIds } from 'approot/shared/inline-svg/inline-svg';
import {
  useImageLoad,
  useResponsiveImage,
} from 'approot/shared/images/images.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  openInWindow,
  createExternalUrl,
  goOrShowModal,
  shouldOpenInNewTab,
} from './content-card.utils';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import * as ContentCardPartsStyled from './content-card-parts.style';
import * as ContentCardStyled from './content-card.styles';
import type { ContentBrowserQuery } from '../../content-browser/content-browser.graphql.generated';
import type { GetUnitDetailsQuery } from '../../unit/unit.graphql.generated';
import type { TopicUnitsFragment } from '../../topic/sections/units/units.graphql.generated';
import { CurriculumFragment } from 'approot/shared/curriculum-tags/curriculum-tags.constants';
import {
  CurriculumLessonTags,
  CurriculumTags,
} from 'approot/shared/curriculum-tags/curriculum-tags';
import type { GetSelectedUnitsByIdQuery } from '../../subject-browser/subject-about/subject-about.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

const Styled = {
  ...ContentCardPartsStyled,
  ...ContentCardStyled,
};

export const ContentCardTags = ({ children }: { children: ReactNode }) => (
  <Styled.ContentCardTags>{children}</Styled.ContentCardTags>
);

export const ContentCardSubjectTags = ({
  subjects,
}: {
  subjects: SubjectFragment[];
}) => {
  return (
    <>
      {subjects.map(subject => (
        <BadgeSmallStyled
          data-selector="subjects-pill"
          key={subject.id}
          $colour={subject.colour}
        >
          {subject.label}
        </BadgeSmallStyled>
      ))}
    </>
  );
};

export const ContentCardYearTags = ({
  years,
}: {
  years: InquisitiveYears[];
}) => {
  const { formatYears } = useYearTranslation();
  if (years.length === 0) return null;
  return (
    <BadgeSmallStyled data-selector="years-pill">
      {formatYears(years)}
    </BadgeSmallStyled>
  );
};

export const ContentCardContentAreaTags = ({
  contentAreas,
}: {
  contentAreas: EnglishContentArea[];
}) => {
  const formattedContentAreas = formatEnglishContentAreasList(contentAreas);
  return (
    <>
      {formattedContentAreas.map(contentArea => (
        <BadgeSmallStyled key={contentArea}>{contentArea}</BadgeSmallStyled>
      ))}
    </>
  );
};

export const ContentCardTag = ({
  children,
  ...rest
}: {
  children: ReactNode;
}) => <BadgeSmallStyled {...rest}>{children}</BadgeSmallStyled>;

export const ContentCardImage = ({
  images,
  ignoreImageOrientation,
  hasDarkOverlay,
  overlayText,
  ratio,
  ...props
}: {
  images?: ResponsiveImage[];
  ratio?: number;
  ignoreImageOrientation?: boolean;
  hasDarkOverlay?: boolean;
  overlayText?: string;
}) => {
  const imageUrl = useResponsiveImage(images || [], 500);

  const { imageHasLoaded, isLandscape } = useImageLoad(imageUrl);

  if (!imageUrl && !hasDarkOverlay) return null;

  return (
    <>
      {imageUrl && (
        <Styled.ContentCardImage
          imageHasLoaded={imageHasLoaded}
          imageUrl={imageUrl}
          isLandscape={isLandscape}
          ratio={ratio}
          ignoreImageOrientation={ignoreImageOrientation}
          {...props}
        />
      )}
      {hasDarkOverlay && (
        <Styled.ContentCardDarkOverlayStyled ratio={ratio}>
          {overlayText && (
            <Styled.ContentCardOverlayTextStyled data-selector="content-card-overlay-text">
              {overlayText}
            </Styled.ContentCardOverlayTextStyled>
          )}
        </Styled.ContentCardDarkOverlayStyled>
      )}
    </>
  );
};

export const ContentCardLink = ({
  href,
  screenReaderText,
  openInTab,
  onClick,
}: {
  href?: string;
  screenReaderText: string;
  openInTab?: boolean;
  onClick: (e: MouseEvent) => void;
}) => {
  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, openInNewTab: boolean) => {
      onClick(e);
      if (isKeyboardModifierClick(e)) return;
      if (href && !goOrShowModal(href)) {
        e.preventDefault();
      } else if (openInNewTab) {
        e.preventDefault();
        openInWindow(`/${process.env.REACT_APP_REGION}${href}`);
      }
    },
    [href, onClick]
  );

  return href ? (
    <>
      {shouldOpenInNewTab(href) ? (
        <Styled.ContentCardAnchorStyled
          href={createExternalUrl(href)}
          target="_blank"
          aria-label={screenReaderText}
          rel="noopener noreferrer"
          onClick={e => handleClick(e, false)}
        >
          <ScreenReaderOnly>{screenReaderText}</ScreenReaderOnly>
        </Styled.ContentCardAnchorStyled>
      ) : (
        <Styled.ContentCardLinkStyled
          to={href}
          aria-label={screenReaderText}
          onClick={e => handleClick(e, !!openInTab)}
        >
          <ScreenReaderOnly>{screenReaderText}</ScreenReaderOnly>
        </Styled.ContentCardLinkStyled>
      )}
    </>
  ) : null;
};

export const ContentCardPill = ({
  icon,
  text,
  ariaHidden,
}: {
  text?: string;
  icon?: InlineSvgIds;
  ariaHidden: boolean;
}) =>
  text ? (
    <Styled.PillContainer aria-hidden={ariaHidden}>
      <Styled.ContentCardPill data-selector="overlay-pill">
        {icon && <Styled.ContentCardIcon id={icon} />}
        {text}
      </Styled.ContentCardPill>
    </Styled.PillContainer>
  ) : null;

export const ContentCardLessonDetails = ({
  years,
  subjects,
  englishContentArea,
  outcomes,
  duration,
  showYearsTag = true,
  showSubjectTag = true,
}: {
  years: InquisitiveYears[];
  subjects: SubjectFragment[];
  englishContentArea?: InquisitiveEnglishContentArea[] | null;
  outcomes?: CurriculumFragment | null;
  duration?: number | null;
  showYearsTag?: boolean;
  showSubjectTag?: boolean;
}) => {
  const { formatYears } = useYearTranslation();

  return (
    <>
      <ContentCardTags>
        {!!duration && (
          <ContentCardTag data-selector="duration-pill">
            {duration} mins
          </ContentCardTag>
        )}
        {years.length > 0 && showYearsTag && (
          <ContentCardTag data-selector="years-pill">
            {formatYears(years)}
          </ContentCardTag>
        )}
        {subjects.length > 0 && showSubjectTag && (
          <ContentCardSubjectTags subjects={subjects} />
        )}
      </ContentCardTags>
      <ContentCardTags>
        {outcomes && <CurriculumLessonTags outcomes={outcomes} />}
      </ContentCardTags>
      {englishContentArea && (
        <ContentCardTags>
          <ContentCardContentAreaTags contentAreas={englishContentArea} />
        </ContentCardTags>
      )}
    </>
  );
};

export const ContentCardUnitDetails = ({
  outcomes,
  years,
  subjects,
  strands,
  showYearsTag = true,
  showSubjectTag = true,
}: {
  outcomes?:
    | Pluck<GetUnitDetailsQuery, 'getUnitById.outcomesByCurriculums'>[]
    | Pluck<TopicUnitsFragment, 'units.outcomesByCurriculums'>[]
    | Pluck<
        GetSelectedUnitsByIdQuery,
        'getSelectedUnitsById.outcomesByCurriculums'
      >[];
  years: InquisitiveYears[];
  subjects: SubjectFragment[];
  strands:
    | Pluck<ContentBrowserQuery, 'contentBrowser.topics.units.strands'>[]
    | null;
  showYearsTag?: boolean;
  showSubjectTag?: boolean;
}) => {
  const { formatYears } = useYearTranslation();

  return (
    <>
      <ContentCardTags>
        {!!strands?.length &&
          sortBy(strands, 'name').map(strand => (
            <ContentCardTag key={strand.id}>{strand.name}</ContentCardTag>
          ))}
        {showSubjectTag && <ContentCardSubjectTags subjects={subjects} />}
        {showYearsTag && (
          <ContentCardTag data-selector="years-pill">
            {formatYears(years)}
          </ContentCardTag>
        )}
      </ContentCardTags>
      <ContentCardTags>
        {outcomes && <CurriculumTags outcomes={outcomes} />}
      </ContentCardTags>
    </>
  );
};
